import { z } from 'zod';
import { lenientString } from '../schema-extensions';
import { PronounType } from '../enums/pronoun-type.enum';

/**
 * Validation constants for PersonPronounNode
 * Defined before schemas to avoid forward reference issues
 */
const PRONOUN_CONSTANTS = {
  PRONOUN_MAX_LENGTH: 20,
} as const;

/**
 * Lenient schema for deserializing PersonPronounNode from storage
 */
const PersonPronounNodeDeserializeSchema = z.union([
  z.object({
    type: z.literal(PronounType.MASCULINE),
    subject: z.literal('he'),
    object: z.literal('him'),
    possessive: z.literal('his')
  }).transform(_val => ({
    type: PronounType.MASCULINE as const,
    subject: 'he' as const,
    object: 'him' as const,
    possessive: 'his' as const
  })),
  z.object({
    type: z.literal(PronounType.FEMININE),
    subject: z.literal('she'),
    object: z.literal('her'),
    possessive: z.literal('hers')
  }).transform(_val => ({
    type: PronounType.FEMININE as const,
    subject: 'she' as const,
    object: 'her' as const,
    possessive: 'hers' as const
  })),
  z.object({
    type: z.literal(PronounType.NEUTRAL),
    subject: z.literal('they'),
    object: z.literal('them'),
    possessive: z.literal('theirs')
  }).transform(_val => ({
    type: PronounType.NEUTRAL as const,
    subject: 'they' as const,
    object: 'them' as const,
    possessive: 'theirs' as const
  })),
  z.object({
    type: z.literal(PronounType.CUSTOM),
    subject: lenientString(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH, ''),
    object: lenientString(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH, ''),
    possessive: lenientString(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH, '')
  }).transform(val => ({
    type: PronounType.CUSTOM as const,
    subject: val.subject,
    object: val.object,
    possessive: val.possessive
  }))
]).catch({
  type: PronounType.NEUTRAL,
  subject: 'they',
  object: 'them',
  possessive: 'theirs'
});

/**
 * Strict schema for creating new PersonPronounNode
 */
const PersonPronounNodeCreateSchema = z.union([
  z.object({
    type: z.literal(PronounType.MASCULINE),
    subject: z.literal('he'),
    object: z.literal('him'),
    possessive: z.literal('his')
  }).transform(_val => ({
    type: PronounType.MASCULINE as const,
    subject: 'he' as const,
    object: 'him' as const,
    possessive: 'his' as const
  })),
  z.object({
    type: z.literal(PronounType.FEMININE),
    subject: z.literal('she'),
    object: z.literal('her'),
    possessive: z.literal('hers')
  }).transform(_val => ({
    type: PronounType.FEMININE as const,
    subject: 'she' as const,
    object: 'her' as const,
    possessive: 'hers' as const
  })),
  z.object({
    type: z.literal(PronounType.NEUTRAL),
    subject: z.literal('they'),
    object: z.literal('them'),
    possessive: z.literal('theirs')
  }).transform(_val => ({
    type: PronounType.NEUTRAL as const,
    subject: 'they' as const,
    object: 'them' as const,
    possessive: 'theirs' as const
  })),
  z.object({
    type: z.literal(PronounType.CUSTOM),
    subject: z.string().min(1).max(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH),
    object: z.string().min(1).max(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH),
    possessive: z.string().min(1).max(PRONOUN_CONSTANTS.PRONOUN_MAX_LENGTH)
  }).transform(val => ({
    type: PronounType.CUSTOM as const,
    subject: val.subject,
    object: val.object,
    possessive: val.possessive
  }))
]);

/**
 * PersonPronounNode type
 * Discriminated union representing different pronoun sets
 */
export type PersonPronounNode =
  | { type: PronounType.MASCULINE; subject: 'he'; object: 'him'; possessive: 'his' }
  | { type: PronounType.FEMININE; subject: 'she'; object: 'her'; possessive: 'hers' }
  | { type: PronounType.NEUTRAL; subject: 'they'; object: 'them'; possessive: 'theirs' }
  | { type: PronounType.CUSTOM; subject: string; object: string; possessive: string };

/**
 * PersonPronounNode namespace with factory and deserialization methods
 */
export namespace PersonPronounNode {
  /**
   * Validation constants for PersonPronounNode
   */
  export const Constants = PRONOUN_CONSTANTS;

  /**
   * Deserialize PersonPronounNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid PersonPronounNode with defaults applied
   */
  export function deserialize(data: unknown): PersonPronounNode {
    return PersonPronounNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize PersonPronounNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: PersonPronounNode }
    | { success: false; error: z.ZodError } 
  {
    const result = PersonPronounNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new PersonPronounNode with strict validation
   * 
   * @param data Partial pronoun data
   * @returns Valid PersonPronounNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<PersonPronounNode>): PersonPronounNode {
    return PersonPronounNodeCreateSchema.parse(data);
  }

  /**
   * Validate if data conforms to PersonPronounNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid PersonPronounNode
   */
  export function validate(data: unknown): data is PersonPronounNode {
    return PersonPronounNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Predefined pronoun sets for common usage
   */
  export const Presets = {
    MASCULINE: { type: PronounType.MASCULINE, subject: 'he', object: 'him', possessive: 'his' } as const,
    FEMININE: { type: PronounType.FEMININE, subject: 'she', object: 'her', possessive: 'hers' } as const,
    NEUTRAL: { type: PronounType.NEUTRAL, subject: 'they', object: 'them', possessive: 'theirs' } as const,
  } as const;
}
